import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_PRIMARY = '#2b3380';

function FinanceManagement({ user, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [activeModule, setActiveModule] = useState(searchParams.get('module') || 'all');
  const [loading, setLoading] = useState(false);

  // Data states
  const [dashboardStats, setDashboardStats] = useState(null);
  const [paymentDemands, setPaymentDemands] = useState([]);
  const [collections, setCollections] = useState([]);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // PHASE 2: Role-Based Tab Configuration
  const isAdmin = user?.role === 'Admin' || user?.role === 'Finance Team' || user?.mobile === '9876543210';
  const userModule = user?.role === 'Franchisee Team' ? 'Franchisee' : 
                      user?.role === 'Purchase Team' ? 'Purchase' : 
                      user?.role === 'HR Team' ? 'HR' : 'all';

  // State for accounting integration
  const [accountingSummary, setAccountingSummary] = useState(null);

  // PHASE 2: Dynamic tabs based on role
  const tabs = useMemo(() => {
    if (isAdmin) {
      return [
        { id: 'overview', name: 'Overview', icon: '📊', desc: 'All modules combined' },
        { id: 'by-module', name: 'By Module', icon: '📁', desc: 'Module-specific view' },
        { id: 'demands', name: 'All Demands', icon: '💰', desc: 'All payment demands' },
        { id: 'collections', name: 'All Collections', icon: '💵', desc: 'All collections' },
        { id: 'accounting', name: 'Accounting', icon: '📒', desc: 'Linked vouchers & ledgers' },
        { id: 'reports', name: 'Reports', icon: '📈', desc: 'Financial reports' }
      ];
    } else {
      // Module-specific user
      return [
        { id: 'dashboard', name: 'Dashboard', icon: '📊', desc: `${userModule} overview` },
        { id: 'demands', name: 'Payment Demands', icon: '💰', desc: 'Your demands' },
        { id: 'collections', name: 'Collections', icon: '💵', desc: 'Your collections' },
        { id: 'reports', name: 'Reports', icon: '📈', desc: 'Your reports' }
      ];
    }
  }, [isAdmin, userModule]);

  // Module tabs for admin's "By Module" view
  const moduleTabs = [
    { id: 'franchisee', name: 'Franchisee', icon: '🏪', color: '#8B5CF6' },
    { id: 'purchase', name: 'Purchase', icon: '🛒', color: '#F59E0B' },
    { id: 'supplier', name: 'Supplier', icon: '🏢', color: '#06B6D4' },
    { id: 'hr', name: 'HR & Payroll', icon: '👥', color: '#10B981' }
  ];

  useEffect(() => {
    const tab = searchParams.get('tab');
    const module = searchParams.get('module');
    if (tab) setActiveTab(tab);
    if (module) setActiveModule(module);
  }, [searchParams]);

  useEffect(() => {
    loadData();
  }, [activeTab, activeModule]);

  const loadData = async () => {
    if (activeTab === 'overview' || activeTab === 'dashboard') {
      await fetchDashboardStats();
    } else if (activeTab === 'by-module') {
      await fetchModuleDashboard(activeModule);
    } else if (activeTab === 'demands') {
      await fetchPaymentDemands();
    } else if (activeTab === 'collections') {
      await fetchCollections();
    } else if (activeTab === 'accounting') {
      await fetchAccountingSummary();
    }
  };

  // Fetch accounting integration summary
  const fetchAccountingSummary = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/finance/accounting-summary`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAccountingSummary(response.data);
    } catch (error) {
      console.error('Error fetching accounting summary:', error);
    } finally {
      setLoading(false);
    }
  };

  // PHASE 2: Fetch with role-based filtering
  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (!isAdmin && userModule !== 'all') {
        params.append('module', userModule);
      }
      const response = await axios.get(`${API}/api/finance-dashboard?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // PHASE 3: Module-specific dashboard
  const fetchModuleDashboard = async (module) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/finance-dashboard?module=${module}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Error fetching module dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentDemands = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (!isAdmin && userModule !== 'all') {
        params.append('source_module', userModule);
      }
      const response = await axios.get(`${API}/api/payment-demands?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPaymentDemands(response.data.demands || []);
    } catch (error) {
      console.error('Error fetching payment demands:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCollections = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/payment-collections`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCollections(response.data.collections || []);
    } catch (error) {
      console.error('Error fetching collections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (tabId === 'by-module' && activeModule === 'all') {
      setActiveModule('franchisee');
      setSearchParams({ tab: tabId, module: 'franchisee' });
    } else {
      setSearchParams({ tab: tabId });
    }
  };

  const handleModuleChange = (moduleId) => {
    setActiveModule(moduleId);
    setSearchParams({ tab: 'by-module', module: moduleId });
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Raised': 'bg-yellow-100 text-yellow-800',
      'Part Paid': 'bg-orange-100 text-orange-800',
      'Fully Paid': 'bg-green-100 text-green-800',
      'Overdue': 'bg-red-100 text-red-800',
      'Cancelled': 'bg-gray-100 text-gray-600'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredDemands = paymentDemands.filter(demand => {
    if (statusFilter !== 'all' && demand.status !== statusFilter) return false;
    if (typeFilter !== 'all' && demand.demand_type !== typeFilter) return false;
    if (dateFrom && demand.due_date && demand.due_date < dateFrom) return false;
    if (dateTo && demand.due_date && demand.due_date > dateTo) return false;
    return true;
  });

  // PHASE 3: Module-specific dashboard component
  const ModuleDashboard = ({ module, stats }) => {
    const moduleConfig = moduleTabs.find(m => m.id === module) || moduleTabs[0];
    
    return (
      <div className="space-y-6">
        {/* Module Header */}
        <div className="rounded-lg shadow p-6" style={{ backgroundColor: `${moduleConfig.color}15`, borderLeft: `4px solid ${moduleConfig.color}` }}>
          <div className="flex items-center gap-3">
            <span className="text-4xl">{moduleConfig.icon}</span>
            <div>
              <h2 className="text-2xl font-bold" style={{ color: moduleConfig.color }}>{moduleConfig.name} Module</h2>
              <p className="text-gray-600">Financial overview and payment tracking</p>
            </div>
          </div>
        </div>

        {/* Module-specific metrics */}
        <div className="grid grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6 border-l-4" style={{ borderColor: moduleConfig.color }}>
            <div className="text-sm text-gray-600 mb-1">Total Revenue</div>
            <div className="text-3xl font-bold" style={{ color: moduleConfig.color }}>₹{stats?.total_revenue?.toLocaleString() || 0}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
            <div className="text-sm text-gray-600 mb-1">Collected</div>
            <div className="text-3xl font-bold text-green-600">₹{stats?.total_revenue?.toLocaleString() || 0}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
            <div className="text-sm text-gray-600 mb-1">Outstanding</div>
            <div className="text-3xl font-bold text-orange-600">₹{stats?.outstanding_payments?.toLocaleString() || 0}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
            <div className="text-sm text-gray-600 mb-1">Overdue</div>
            <div className="text-3xl font-bold text-red-600">₹{stats?.overdue_payments?.toLocaleString() || 0}</div>
          </div>
        </div>

        {/* Recent activity */}
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Collections</h3>
            {stats?.recent_collections && stats.recent_collections.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_collections.slice(0, 5).map((collection) => (
                  <div key={collection.collection_id} className="flex justify-between items-start pb-3 border-b">
                    <div>
                      <div className="font-medium text-gray-900">{collection.demand_type}</div>
                      <div className="text-xs text-gray-500">{new Date(collection.payment_date).toLocaleDateString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-green-600">₹{collection.amount_paid?.toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-4">No recent collections</div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Pending Payments</h3>
            {stats?.upcoming_due && stats.upcoming_due.length > 0 ? (
              <div className="space-y-3">
                {stats.upcoming_due.slice(0, 5).map((demand) => (
                  <div key={demand.demand_id} className="flex justify-between items-start pb-3 border-b">
                    <div>
                      <div className="font-medium text-gray-900">{demand.demand_type}</div>
                      <div className="text-xs text-red-500">Due: {new Date(demand.due_date).toLocaleDateString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-orange-600">₹{demand.amount_outstanding?.toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-4">No pending payments</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Finance Management</h1>
          <p className="mt-2 text-gray-600">
            {isAdmin ? 'Centralized payment tracking across all modules' : `${userModule} payment tracking and collections`}
          </p>
        </div>

        {/* Main Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-transparent text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                style={activeTab === tab.id ? { borderColor: ZOBOX_PRIMARY, color: ZOBOX_PRIMARY } : {}}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* PHASE 1: Module Sub-tabs for Admin */}
        {isAdmin && activeTab === 'by-module' && (
          <div className="mb-6 bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Select Module:</span>
              {moduleTabs.map((module) => (
                <button
                  key={module.id}
                  onClick={() => handleModuleChange(module.id)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    activeModule === module.id ? 'shadow-md' : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                  style={
                    activeModule === module.id
                      ? { backgroundColor: `${module.color}15`, color: module.color, border: `2px solid ${module.color}` }
                      : {}
                  }
                >
                  <span className="mr-2">{module.icon}</span>
                  {module.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: ZOBOX_PRIMARY }}></div>
          </div>
        ) : (
          <>
            {/* Overview Dashboard (Admin - All Modules) */}
            {activeTab === 'overview' && dashboardStats && (
              <div className="space-y-6">
                {/* Key Metrics */}
                <div className="grid grid-cols-4 gap-6">
                  <div className="rounded-lg shadow-lg p-6 text-white" style={{ background: 'linear-gradient(to right, #2b3380, #1f2660)' }}>
                    <div className="text-sm opacity-90 mb-1">Total Revenue</div>
                    <div className="text-4xl font-bold">₹{dashboardStats.total_revenue?.toLocaleString() || 0}</div>
                    <div className="text-xs opacity-75 mt-1">All modules combined</div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
                    <div className="text-sm text-gray-600 mb-1">Today's Collections</div>
                    <div className="text-4xl font-bold text-green-600">₹{dashboardStats.today_collections?.toLocaleString() || 0}</div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
                    <div className="text-sm text-gray-600 mb-1">Outstanding</div>
                    <div className="text-4xl font-bold text-orange-600">₹{dashboardStats.outstanding_payments?.toLocaleString() || 0}</div>
                    <div className="text-xs text-gray-500 mt-1">{dashboardStats.pending_demands || 0} pending</div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
                    <div className="text-sm text-gray-600 mb-1">Overdue</div>
                    <div className="text-4xl font-bold text-red-600">₹{dashboardStats.overdue_payments?.toLocaleString() || 0}</div>
                    <div className="text-xs text-gray-500 mt-1">{dashboardStats.overdue_demands || 0} overdue</div>
                  </div>
                </div>

                {/* By Module Breakdown */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-4">Revenue by Module</h3>
                  <div className="grid grid-cols-4 gap-4">
                    {moduleTabs.map((module) => (
                      <div key={module.id} className="border rounded-lg p-4" style={{ borderLeft: `4px solid ${module.color}` }}>
                        <div className="text-2xl mb-2">{module.icon}</div>
                        <div className="text-sm text-gray-600">{module.name}</div>
                        <div className="text-2xl font-bold mt-2" style={{ color: module.color }}>
                          ₹{dashboardStats.by_module?.[module.name]?.toLocaleString() || 0}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Collections & Upcoming Due */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold mb-4">Recent Collections</h3>
                    <div className="space-y-3">
                      {dashboardStats.recent_collections && dashboardStats.recent_collections.length > 0 ? (
                        dashboardStats.recent_collections.slice(0, 5).map((collection) => (
                          <div key={collection.collection_id} className="flex justify-between items-start pb-3 border-b">
                            <div>
                              <div className="font-medium text-gray-900">{collection.demand_type}</div>
                              <div className="text-xs text-gray-500">{collection.source_name}</div>
                              <div className="text-xs text-gray-500">{new Date(collection.payment_date).toLocaleDateString()}</div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-green-600">₹{collection.amount_paid?.toLocaleString()}</div>
                              <div className="text-xs text-gray-500">{collection.payment_method}</div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-gray-500 py-4">No recent collections</div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold mb-4">Upcoming Due Payments</h3>
                    <div className="space-y-3">
                      {dashboardStats.upcoming_due && dashboardStats.upcoming_due.length > 0 ? (
                        dashboardStats.upcoming_due.slice(0, 5).map((demand) => (
                          <div key={demand.demand_id} className="flex justify-between items-start pb-3 border-b">
                            <div>
                              <div className="font-medium text-gray-900">{demand.demand_type}</div>
                              <div className="text-xs text-gray-500">{demand.source_name}</div>
                              <div className="text-xs text-red-500">Due: {new Date(demand.due_date).toLocaleDateString()}</div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-orange-600">₹{demand.amount_outstanding?.toLocaleString()}</div>
                              <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadgeColor(demand.status)}`}>
                                {demand.status}
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-gray-500 py-4">No upcoming payments</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* PHASE 3: By Module Dashboard */}
            {activeTab === 'by-module' && dashboardStats && (
              <ModuleDashboard module={activeModule} stats={dashboardStats} />
            )}

            {/* PHASE 2: Module-specific Dashboard (for non-admin users) */}
            {!isAdmin && activeTab === 'dashboard' && dashboardStats && (
              <ModuleDashboard module={userModule.toLowerCase()} stats={dashboardStats} />
            )}

            {/* Payment Demands Tab */}
            {activeTab === 'demands' && (
              <div className="space-y-6">
                {/* Filters */}
                <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="all">All Status</option>
                      <option value="Raised">Raised</option>
                      <option value="Part Paid">Part Paid</option>
                      <option value="Fully Paid">Fully Paid</option>
                      <option value="Overdue">Overdue</option>
                    </select>

                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="all">All Types</option>
                      <option value="Token Payment">Token Payment</option>
                      <option value="Franchisee Fee">Franchisee Fee</option>
                      <option value="Store Infrastructure">Store Infrastructure</option>
                      <option value="Stock Payment">Stock Payment</option>
                      <option value="Supplier Payment">Supplier Payment</option>
                    </select>

                    {(statusFilter !== 'all' || typeFilter !== 'all') && (
                      <button
                        onClick={() => {
                          setStatusFilter('all');
                          setTypeFilter('all');
                        }}
                        className="text-sm text-gray-600 hover:text-gray-900"
                      >
                        ✕ Clear
                      </button>
                    )}
                  </div>

                  <div className="text-sm text-gray-600">
                    Showing {filteredDemands.length} of {paymentDemands.length} demands
                  </div>
                </div>

                {/* Demands Table */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  {filteredDemands.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">💰</div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Payment Demands</h3>
                      <p className="text-gray-600">No demands found matching your filters</p>
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Demand #</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredDemands.map((demand) => (
                          <tr key={demand.demand_id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: ZOBOX_PRIMARY }}>
                              {demand.demand_number}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{demand.demand_type}</td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              <div>{demand.source_name}</div>
                              <div className="text-xs text-gray-500">{demand.source_module}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">₹{demand.amount?.toLocaleString()}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600 font-semibold">₹{demand.amount_outstanding?.toLocaleString()}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {demand.due_date ? new Date(demand.due_date).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeColor(demand.status)}`}>
                                {demand.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {/* Collections Tab */}
            {activeTab === 'collections' && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                {collections.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">💵</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Collections Yet</h3>
                    <p className="text-gray-600">Collections will appear here once payments are received</p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt #</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Voucher</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {collections.map((collection) => (
                        <tr key={collection.collection_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: ZOBOX_PRIMARY }}>
                            {collection.receipt_number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{collection.demand_type}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                            ₹{collection.amount_paid?.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{collection.payment_method}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {new Date(collection.payment_date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {collection.accounting_voucher_number ? (
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                                📒 {collection.accounting_voucher_number}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Accounting Integration Tab */}
            {activeTab === 'accounting' && (
              <div className="space-y-6">
                {/* Accounting Summary Cards */}
                {accountingSummary?.summary && (
                  <div className="grid grid-cols-4 gap-6">
                    <div className="bg-white rounded-lg shadow p-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm text-gray-600">Total Receivables</span>
                        <span className="text-2xl">📥</span>
                      </div>
                      <div className="text-2xl font-bold text-blue-600">
                        ₹{accountingSummary.summary.total_receivables?.toLocaleString() || 0}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {accountingSummary.summary.debtors_count || 0} debtor ledgers
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm text-gray-600">Total Payables</span>
                        <span className="text-2xl">📤</span>
                      </div>
                      <div className="text-2xl font-bold text-orange-600">
                        ₹{accountingSummary.summary.total_payables?.toLocaleString() || 0}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {accountingSummary.summary.creditors_count || 0} creditor ledgers
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm text-gray-600">Net Position</span>
                        <span className="text-2xl">⚖️</span>
                      </div>
                      <div className={`text-2xl font-bold ${(accountingSummary.summary.net_position || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ₹{accountingSummary.summary.net_position?.toLocaleString() || 0}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        Receivables - Payables
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm text-gray-600">Linked Collections</span>
                        <span className="text-2xl">🔗</span>
                      </div>
                      <div className="text-2xl font-bold text-purple-600">
                        {accountingSummary.summary.linked_collections_count || 0}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        Collections with vouchers
                      </div>
                    </div>
                  </div>
                )}

                {/* Recent Vouchers */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">📒 Recent Accounting Vouchers</h3>
                    <a 
                      href="/admin/accounting?tab=vouchers" 
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View All →
                    </a>
                  </div>
                  {accountingSummary?.recent_vouchers?.length > 0 ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Voucher #</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Party</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {accountingSummary.recent_vouchers.map((voucher, idx) => (
                          <tr key={voucher.id || idx} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: ZOBOX_PRIMARY }}>
                              {voucher.voucher_number}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                voucher.voucher_type === 'Receipt' ? 'bg-green-100 text-green-800' :
                                voucher.voucher_type === 'Payment' ? 'bg-orange-100 text-orange-800' :
                                voucher.voucher_type === 'Purchase' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {voucher.voucher_type}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {voucher.party_name || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                              ₹{voucher.total_amount?.toLocaleString() || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {voucher.voucher_date ? new Date(voucher.voucher_date).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                voucher.status === 'Approved' ? 'bg-green-100 text-green-800' :
                                voucher.status === 'Draft' ? 'bg-gray-100 text-gray-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {voucher.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">📒</div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Vouchers Yet</h3>
                      <p className="text-gray-600">Accounting vouchers will appear here once created</p>
                    </div>
                  )}
                </div>

                {/* Linked Collections */}
                {accountingSummary?.linked_collections?.length > 0 && (
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-900">🔗 Collections Linked to Accounting</h3>
                    </div>
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt #</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Voucher #</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {accountingSummary.linked_collections.map((collection, idx) => (
                          <tr key={collection.collection_id || idx} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: ZOBOX_PRIMARY }}>
                              {collection.receipt_number}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                              ₹{collection.amount_paid?.toLocaleString() || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {collection.payment_method || collection.payment_mode}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {collection.payment_date ? new Date(collection.payment_date).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                                📒 {collection.accounting_voucher_number}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Reports Tab */}
            {activeTab === 'reports' && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Financial Reports</h3>
                <div className="grid grid-cols-3 gap-6">
                  <button className="p-6 border-2 border-gray-200 rounded-lg hover:border-gray-300 text-left">
                    <div className="text-3xl mb-2">📊</div>
                    <div className="font-semibold text-gray-900">Payment Register</div>
                    <div className="text-sm text-gray-600 mt-1">All payment demands and collections</div>
                  </button>

                  <button className="p-6 border-2 border-gray-200 rounded-lg hover:border-gray-300 text-left">
                    <div className="text-3xl mb-2">💰</div>
                    <div className="font-semibold text-gray-900">Outstanding Report</div>
                    <div className="text-sm text-gray-600 mt-1">Pending and overdue payments</div>
                  </button>

                  <button className="p-6 border-2 border-gray-200 rounded-lg hover:border-gray-300 text-left">
                    <div className="text-3xl mb-2">📈</div>
                    <div className="font-semibold text-gray-900">Collection Report</div>
                    <div className="text-sm text-gray-600 mt-1">Daily, monthly collections summary</div>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

export default FinanceManagement;
